import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { ArrowLeft, Plus, RefreshCw, Loader2, ChevronDown, ChevronUp, ShoppingBag, Users, DollarSign, Package, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const PRODUCT_TYPES = ['tee', 'hoodie', 'tank', 'longsleeve', 'crewneck', 'hat', 'sticker', 'banner', 'other'];

const DROP_STATUS_CONFIG = {
  active:    { color: '#10B981', bg: '#ECFDF5', border: '#86EFAC' },
  closed:    { color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' },
  fulfilled: { color: '#7C3AED', bg: '#F5F3FF', border: '#C4B5FD' },
  draft:     { color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D' },
};

export default function MerchManager() {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [orders, setOrders] = useState({});
  const [newDrop, setNewDrop] = useState({
    title: '', description: '', product_type: 'tee', goal_quantity: 50, deadline: '',
    price_tiers: [{ min_qty: 1, price: 35 }, { min_qty: 24, price: 30 }, { min_qty: 48, price: 25 }, { min_qty: 72, price: 22 }],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/merch/drops'); setDrops(r.data.drops || []); }
    catch { toast.error('Failed to load drops'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadOrders = async (dropId) => {
    try { const r = await api.get(`/merch/drops/${dropId}/orders`); setOrders(prev => ({ ...prev, [dropId]: r.data.orders || [] })); }
    catch { toast.error('Failed to load orders'); }
  };

  const handleCreate = async () => {
    if (!newDrop.title) { toast.error('Title required'); return; }
    try {
      await api.post('/merch/drops', newDrop);
      toast.success(`Drop created: ${newDrop.title}`);
      setCreating(false);
      setNewDrop({ title: '', description: '', product_type: 'tee', goal_quantity: 50, deadline: '', price_tiers: [{ min_qty: 1, price: 35 }, { min_qty: 24, price: 30 }, { min_qty: 48, price: 25 }, { min_qty: 72, price: 22 }] });
      load();
    } catch { toast.error('Failed to create'); }
  };

  const updateStatus = async (dropId, status) => {
    try { await api.patch(`/merch/drops/${dropId}`, { status }); load(); toast.success(`Status: ${status}`); }
    catch { toast.error('Failed'); }
  };

  const toggleExpand = (dropId) => {
    if (expanded === dropId) { setExpanded(null); return; }
    setExpanded(dropId);
    if (!orders[dropId]) loadOrders(dropId);
  };

  const updateTier = (idx, field, value) => {
    setNewDrop(d => ({ ...d, price_tiers: d.price_tiers.map((t, i) => i === idx ? { ...t, [field]: parseInt(value) || 0 } : t) }));
  };

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      <div className="border-b border-gray-200 sticky top-0 z-40 bg-white/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <Link to="/staff/huscleos" className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"><ArrowLeft size={16} /> Pipeline</Link>
            <div className="w-px h-5 bg-gray-200" />
            <div className="flex items-center gap-1">
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#0F0F0F' }}>HUSCLE</span>
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#FF2E00' }}>OS</span>
              <span className="text-xs font-bold tracking-widest uppercase text-gray-500 ml-2">MERCH</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/merch" target="_blank" className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-gray-900"><ExternalLink size={14} /> Public Page</Link>
            <button onClick={load} className="p-2 text-gray-500 hover:text-gray-900"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /></button>
            <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-4 py-2 bg-[#FF2E00] text-white hover:bg-[#E02900]" data-testid="create-drop-btn"><Plus size={14} /> New Drop</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Create form */}
        {creating && (
          <div className="mb-6 p-6 bg-white border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold tracking-widest uppercase text-gray-900 mb-4">New Merch Drop</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <input placeholder="Drop Title *" value={newDrop.title} onChange={e => setNewDrop(d => ({ ...d, title: e.target.value }))} data-testid="drop-title"
                className="p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 focus:border-[#FF2E00] focus:outline-none" />
              <select value={newDrop.product_type} onChange={e => setNewDrop(d => ({ ...d, product_type: e.target.value }))} data-testid="drop-type"
                className="p-3 text-sm text-gray-900 bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#FF2E00]">
                {PRODUCT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
              <input placeholder="Goal Quantity" type="number" value={newDrop.goal_quantity} onChange={e => setNewDrop(d => ({ ...d, goal_quantity: parseInt(e.target.value) || 50 }))} data-testid="drop-goal"
                className="p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#FF2E00]" />
              <input type="date" value={newDrop.deadline} onChange={e => setNewDrop(d => ({ ...d, deadline: e.target.value }))} data-testid="drop-deadline"
                className="p-3 text-sm text-gray-900 bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#FF2E00]" />
            </div>
            <textarea placeholder="Description (optional)" value={newDrop.description} onChange={e => setNewDrop(d => ({ ...d, description: e.target.value }))}
              className="w-full p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-gray-50 border border-gray-200 focus:outline-none focus:border-[#FF2E00] mb-4 resize-none" rows={2} />
            <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">PRICE TIERS</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {newDrop.price_tiers.map((t, i) => (
                <div key={i} className="p-2 border border-gray-200 bg-gray-50">
                  <input type="number" value={t.min_qty} onChange={e => updateTier(i, 'min_qty', e.target.value)} className="w-full mb-1 p-1.5 text-xs text-gray-900 bg-white border border-gray-200 focus:outline-none focus:border-[#FF2E00]" placeholder="Min qty" />
                  <input type="number" value={t.price} onChange={e => updateTier(i, 'price', e.target.value)} className="w-full p-1.5 text-xs text-gray-900 bg-white border border-gray-200 focus:outline-none focus:border-[#FF2E00]" placeholder="Price $" />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={handleCreate} className="text-xs font-bold tracking-widest uppercase px-5 py-2.5 bg-[#FF2E00] text-white hover:bg-[#E02900]" data-testid="save-drop-btn">Create Drop</button>
              <button onClick={() => setCreating(false)} className="text-xs font-bold tracking-widest uppercase px-5 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        )}

        {loading && drops.length === 0 ? (
          <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
        ) : drops.length === 0 ? (
          <div className="text-center py-20"><ShoppingBag size={32} className="mx-auto mb-3 text-gray-300" /><p className="text-gray-500 mb-3">No merch drops yet.</p>
            <button onClick={() => setCreating(true)} className="text-sm font-bold text-[#FF2E00] hover:underline">Create your first drop</button>
          </div>
        ) : (
          <div className="space-y-3">
            {drops.map(drop => {
              const isOpen = expanded === drop.id;
              const pct = drop.goal_quantity > 0 ? Math.min(100, Math.round((drop.total_units / drop.goal_quantity) * 100)) : 0;
              const dropOrders = orders[drop.id] || [];
              const sc = DROP_STATUS_CONFIG[drop.status] || DROP_STATUS_CONFIG.draft;

              return (
                <div key={drop.id} className="bg-white border border-gray-200 overflow-hidden" data-testid={`drop-${drop.id}`}>
                  <div className="p-5 cursor-pointer flex items-center gap-4 hover:bg-gray-50 transition-colors" onClick={() => toggleExpand(drop.id)}>
                    <ShoppingBag size={18} className="text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <h3 className="text-sm font-bold text-gray-900 truncate">{drop.title}</h3>
                        <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border" style={{ color: sc.color, background: sc.bg, borderColor: sc.border }}>{drop.status}</span>
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide">{drop.product_type}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span><Users size={12} className="inline mr-1" />{drop.total_orders || 0} orders</span>
                        <span><Package size={12} className="inline mr-1" />{drop.total_units || 0}/{drop.goal_quantity} units</span>
                        <span><DollarSign size={12} className="inline mr-1" />${drop.total_revenue || 0}</span>
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-3 shrink-0">
                      <div className="w-24 h-2 bg-gray-100 overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: pct >= 100 ? '#10B981' : '#FF2E00' }} /></div>
                      <span className="text-xs font-bold text-gray-500 w-10 tabular-nums">{pct}%</span>
                    </div>
                    {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>

                  {isOpen && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4 bg-[#FAFAFA]">
                      {/* Status controls */}
                      <div className="flex items-center flex-wrap gap-2 mb-4">
                        <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500">Status:</span>
                        {['active', 'closed', 'fulfilled'].map(s => {
                          const sc2 = DROP_STATUS_CONFIG[s];
                          const active = drop.status === s;
                          return (
                            <button key={s} onClick={() => updateStatus(drop.id, s)}
                              className={`text-[10px] font-bold tracking-widest uppercase px-3 py-1 border transition-all ${active ? '' : 'opacity-60 hover:opacity-100'}`}
                              style={{ color: sc2.color, background: active ? sc2.bg : 'white', borderColor: active ? sc2.border : '#E5E7EB' }}>{s}</button>
                          );
                        })}
                      </div>

                      {/* Price tiers */}
                      <div className="mb-4">
                        <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Price Tiers</p>
                        <div className="flex flex-wrap gap-2">
                          {(drop.price_tiers || []).map((t, i) => (
                            <div key={i} className="px-3 py-2 border border-gray-200 bg-white">
                              <p className="text-xs font-bold text-gray-900">${t.price}<span className="text-gray-500 font-normal"> / unit</span></p>
                              <p className="text-[10px] text-gray-500">{t.min_qty}+ units</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Orders table */}
                      <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Orders ({dropOrders.length})</p>
                      {dropOrders.length === 0 ? (
                        <p className="text-xs text-gray-400 p-3">No orders yet.</p>
                      ) : (
                        <div className="overflow-x-auto bg-white border border-gray-200">
                          <table className="w-full text-xs">
                            <thead><tr className="text-gray-500 border-b border-gray-200 bg-gray-50">
                              <th className="text-left py-2 px-3 font-bold tracking-wide uppercase">Name</th><th className="text-left py-2 px-3 font-bold tracking-wide uppercase">Email</th>
                              <th className="text-center py-2 px-3 font-bold tracking-wide uppercase">Size</th><th className="text-center py-2 px-3 font-bold tracking-wide uppercase">Qty</th>
                              <th className="text-center py-2 px-3 font-bold tracking-wide uppercase">Price</th><th className="text-center py-2 px-3 font-bold tracking-wide uppercase">Total</th>
                              <th className="text-center py-2 px-3 font-bold tracking-wide uppercase">Status</th>
                            </tr></thead>
                            <tbody>
                              {dropOrders.map(o => (
                                <tr key={o.id} className="border-b border-gray-100 text-gray-700">
                                  <td className="py-2 px-3">{o.name}</td><td className="py-2 px-3">{o.email}</td>
                                  <td className="py-2 px-3 text-center">{o.size}</td><td className="py-2 px-3 text-center">{o.quantity}</td>
                                  <td className="py-2 px-3 text-center tabular-nums">${o.unit_price}</td><td className="py-2 px-3 text-center font-bold tabular-nums">${o.total_price}</td>
                                  <td className="py-2 px-3 text-center"><span className={`px-2 py-0.5 text-[10px] font-bold border ${o.status === 'confirmed' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{o.status}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
